import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clearAuth, saveAuth } from '../api/auth'
import { ApiError, NetworkError, api } from '../api/client'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { ErrorBox } from '../components/common/Feedback'
import s from './LoginPage.module.scss'
import { LOGO } from '../assets'

/**
 * Das Gerüst der Anmeldung — dunkler Grund, weiße Karte, Marke.
 *
 * Steht getrennt, weil ZWEI Seiten es benutzen: die gewöhnliche Anmeldung und
 * die des OAuth-Ablaufs. Eine nachgebaute zweite Fassung liefe bei der
 * nächsten Änderung auseinander, und der Nutzer sähe ausgerechnet dort etwas
 * Fremdes, wo er sein Passwort eintippt.
 */
export function LoginFrame({ children, ...rest }: {
  children: ReactNode
} & React.FormHTMLAttributes<HTMLFormElement>) {
  return (
    <div className={s.screen}>
      <form className={s.form} {...rest}>
        <div className={s.brand}>
          <img src={LOGO} alt="" />
          <div className={s.brandText}>
            <span className={s.brandName}>Instrument Watcher</span>
            <span className={s.brandSub}>Marktdaten</span>
          </div>
        </div>
        {children}
      </form>
    </div>
  )
}

/**
 * Die Anmeldung.
 *
 * Geprüft wird gegen `GET /api/ui/v1/session`. Der Server antwortet dort auf
 * 401 bewusst OHNE `WWW-Authenticate` — sonst übernähme der Browser mit seinem
 * eigenen Dialog, und diese Seite käme nie zum Vorschein.
 *
 * Die Zugangsdaten müssen VOR dem Aufruf gespeichert sein, weil der Client
 * seinen Header von dort nimmt. Schlägt die Prüfung fehl, werden sie wieder
 * entfernt: Sonst läge ein falscher Header im Speicher, und der nächste Aufruf
 * scheiterte an etwas, das der Anmeldende längst korrigiert hat.
 */
export function LoginPage() {
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Wohin nach der Anmeldung: zurück zu der Seite, die den Anmeldenden
  // hergeschickt hat. Ohne das landet er immer auf der Startseite, auch wenn
  // er eigentlich woanders hinwollte.
  //
  // MIT den Abfrageparametern: Seit der Listenzustand in der Adresse steht,
  // trägt ein weitergegebener Link Suche, Filter und Seite mit sich. Nur den
  // Pfad zu übernehmen hiesse, dass ein geteilter Link nach der Anmeldung auf
  // der ungefilterten Liste endet — und niemand sieht, was gemeint war.
  const from = (location.state as
    { from?: { pathname: string; search?: string } } | null)?.from
  const target = from ? `${from.pathname}${from.search ?? ''}` : '/'

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!user || !password) {
      setError('Benutzer und Kennwort sind erforderlich.')
      return
    }

    setBusy(true)
    setError(null)
    saveAuth(user, password)
    try {
      await api.session()
      navigate(target, { replace: true })
    } catch (cause) {
      clearAuth()
      // Nur 401 heißt "Deine Eingabe stimmt nicht". Alles andere ist nicht die
      // Schuld des Anmeldenden — würde dort "Kennwort falsch" stehen, suchte er
      // den Fehler bei sich, während der Dienst aus ist.
      if (cause instanceof NetworkError) {
        setError('Der Server ist nicht erreichbar.')
      } else if (cause instanceof ApiError && cause.status === 401) {
        setError('Benutzer oder Kennwort ist falsch.')
      } else if (cause instanceof ApiError && cause.status >= 500) {
        setError(`Der Server antwortet nicht (${cause.status}). Bitte später erneut versuchen.`)
      } else {
        const status = cause instanceof ApiError ? ` (${cause.status})` : ''
        setError(`Anmeldung fehlgeschlagen${status}.`)
      }
      setBusy(false)
    }
  }

  return (
    <LoginFrame onSubmit={submit}>
      <Input label="Benutzer" value={user} autoFocus autoComplete="username"
             disabled={busy}
             onChange={(event) => setUser(event.target.value)} />
      <Input label="Kennwort" type="password" value={password}
             autoComplete="current-password" disabled={busy}
             onChange={(event) => setPassword(event.target.value)} />

      {error && <ErrorBox>{error}</ErrorBox>}

      <Button type="submit" variant="primary" block disabled={busy}>
        {busy ? 'prüfe …' : 'Anmelden'}
      </Button>
    </LoginFrame>
  )
}
